import { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  LabelList
} from 'recharts';

const Desempeno = () => {
  const [timeRange, setTimeRange] = useState('30D');
  const [dateRange] = useState('Jan 01, 2025 - Feb 20, 2025');

  // Datos para Top 5 mejor performance
  const mejorPerformance = [
    { name: 'Juan Pablo Cepeda', value: 92 },
    { name: 'Diego Alonzo Chá', value: 80 },
    { name: 'Diego Rodriguez F', value: 73 },
    { name: 'Francesco Lucchesi', value: 55 },
    { name: 'Vincenzo Lucchesi', value: 32 }
  ];

  // Datos para Bottom 5 peor performance
  const peorPerformance = [
    { name: 'Demo Ucal', value: 8 },
    { name: 'Erika Palacios', value: 20 },
    { name: 'Valeria Alexandra', value: 28 },
    { name: 'Nayeli Camilla Mo', value: 28 },
    { name: 'Camila Orbegoz', value: 31 }
  ];

  // Datos para puntaje promedio por carrera
  const puntajePromedio = [
    { name: 'Ciencias de la\nComunicación', value: 85, color: '#024579' },
    { name: 'Arquitectura', value: 78, color: '#028bbf' },
    { name: 'Marketing', value: 72, color: '#00bf63' },
    { name: 'Administración', value: 68, color: '#024579' },
    { name: 'Psicología', value: 65, color: '#028bbf' }
  ].sort((a, b) => b.value - a.value);

  // Datos para tendencia de mejora
  const tendenciaMejora = [
    { mes: 'Ene', promedio: 75 },
    { mes: 'Feb', promedio: 78 },
    { mes: 'Mar', promedio: 82 },
    { mes: 'Abr', promedio: 85 },
    { mes: 'May', promedio: 88 },
    { mes: 'Jun', promedio: 92 }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Desempeño de Estudiantes</h1>
          <p className="mt-1 text-sm text-gray-500">
            Análisis detallado del rendimiento individual y grupal
          </p>
          <p className="mt-1 text-sm text-gray-400">{dateRange}</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setTimeRange('7D')}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              timeRange === '7D'
                ? 'text-primary bg-primary/5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            7D
          </button>
          <button
            onClick={() => setTimeRange('30D')}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              timeRange === '30D'
                ? 'text-primary bg-primary/5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            30D
          </button>
          <button
            onClick={() => setTimeRange('90D')}
            className={`px-3 py-1 text-sm font-medium rounded-md ${
              timeRange === '90D'
                ? 'text-primary bg-primary/5'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            90D
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Estudiantes con mejor performance */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900">Top 5 con mejor performance</h2>
          <div className="mt-6 space-y-4">
            {mejorPerformance.map((estudiante) => (
              <div key={estudiante.name} className="relative">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-900">{estudiante.name}</div>
                  <div className="text-sm font-medium text-gray-500">{estudiante.value}</div>
                </div>
                <div className="mt-2 h-2 bg-gray-100 rounded-full">
                  <div
                    className="h-2 bg-success rounded-full"
                    style={{ width: `${estudiante.value}%`, backgroundColor: '#00bf63' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Estudiantes con peor performance */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900">Bottom 5 con peor performance</h2>
          <div className="mt-6 space-y-4">
            {peorPerformance.map((estudiante) => (
              <div key={estudiante.name} className="relative">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-900">{estudiante.name}</div>
                  <div className="text-sm font-medium text-gray-500">{estudiante.value}</div>
                </div>
                <div className="mt-2 h-2 bg-gray-100 rounded-full">
                  <div
                    className="h-2 rounded-full"
                    style={{ width: `${estudiante.value}%`, backgroundColor: '#028bbf' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Puntaje promedio por carrera */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900">Puntaje promedio por carrera</h2>
          <div className="mt-6" style={{ height: '480px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={puntajePromedio}
                margin={{ top: 20, right: 40, left: 40, bottom: 120 }}
                barSize={50}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  interval={0}
                  height={120}
                  textAnchor="middle"
                  tick={(props) => {
                    const { x, y, payload } = props;
                    const lines = payload.value.split('\n');
                    
                    return (
                      <g transform={`translate(${x},${y + 20})`}>
                        {lines.map((line, index) => (
                          <text
                            key={index}
                            x={0}
                            y={0}
                            dy={index * 20}
                            textAnchor="middle"
                            fill="#666"
                            fontSize={13}
                          >
                            {line}
                          </text>
                        ))}
                      </g>
                    );
                  }}
                />
                <YAxis 
                  domain={[0, 100]}
                  label={{ value: 'Puntaje', angle: -90, position: 'insideLeft', offset: 5 }}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip />
                <Bar dataKey="value" fill={(data) => data.color}>
                  <LabelList dataKey="value" position="top" fontSize={14} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Tendencia de mejora */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900">Tendencia de mejora (últimos 90 días)</h2>
          <div className="mt-6" style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={tendenciaMejora}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="mes" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="promedio"
                  name="Promedio"
                  stroke="#024579"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Desempeno; 